import React, { useEffect, useState, useMemo } from "react";
import api from "../api/client";
import { useLocation } from "react-router-dom";
import { queryParam } from "../components/links";
import { ExportCsvButton } from "../components/PageTools";
import { Card, PageHeader, Badge, Money, StatCard } from "../components/ui";
import t from "../components/theme";

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtTimestamp = (v) => v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtUSD = (v) => v != null ? Math.abs(Number(v)).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "—";

export default function Ledger() {
  const [entries, setEntries] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  // A search link opens the journal, the one tab the search filters.
  const [tab, setTab] = useState(() => (queryParam(window.location.search) ? "journal" : "balance-sheet"));
  const [loading, setLoading] = useState(true);
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [expandedAcct, setExpandedAcct] = useState(null);
  const [acctEntries, setAcctEntries] = useState([]);
  const [acctTxnExpanded, setAcctTxnExpanded] = useState(null);
  // Seeded from `?q=`, so a deal report or the global search can open the
  // ledger on one deal's entries (usability review of 23 October 2026, item 3).
  const location = useLocation();
  const [search, setSearch] = useState(() => queryParam(location.search));
  useEffect(() => {
    const q = queryParam(location.search);
    setSearch(q);
    if (q) setTab("journal");
  }, [location.search]);
  const [filterSide, setFilterSide] = useState("");
  const [filterAccount, setFilterAccount] = useState("");

  // Always load all entries (needed for both journal and trial balance drill-down)
  useEffect(() => {
    api.get("/ledger/entries/?limit=2000").then((r) => {
      setEntries(r.data.results || r.data);
    });
  }, []);

  useEffect(() => {
    setExpandedTxn(null);
    setExpandedAcct(null);
    if (tab === "trial-balance") {
      setLoading(true);
      api.get("/ledger/entries/trial-balance/").then((r) => {
        setTrialBalance(r.data);
        setLoading(false);
      });
    } else {
      // All other tabs compute from entries (already loaded)
      setLoading(entries.length === 0);
    }
  }, [tab, entries.length]);

  // Group entries by transaction_id for expand
  const txnMap = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!e.transaction_id) return;
      if (!map[e.transaction_id]) map[e.transaction_id] = [];
      map[e.transaction_id].push(e);
    });
    return map;
  }, [entries]);

  // Unique accounts for filter
  const accounts = useMemo(
    () => [...new Set(entries.map((e) => e.account).filter(Boolean))].sort(),
    [entries]
  );

  // Filter + search
  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterSide && e.side !== filterSide) return false;
      if (filterAccount && e.account !== filterAccount) return false;
      if (search) {
        const s = search.toLowerCase();
        const match =
          (e.description || "").toLowerCase().includes(s) ||
          (e.transaction_id || "").toLowerCase().includes(s) ||
          (e.account || "").toLowerCase().includes(s) ||
          (e.deal_name || "").toLowerCase().includes(s) ||
          (e.entity_name || "").toLowerCase().includes(s);
        if (!match) return false;
      }
      return true;
    });
  }, [entries, filterSide, filterAccount, search]);

  // KPI calculations
  const totalDebits = useMemo(
    () => filtered.filter((e) => e.side === "Debit").reduce((s, e) => s + Number(e.amount || 0), 0),
    [filtered]
  );
  const totalCredits = useMemo(
    () => filtered.filter((e) => e.side === "Credit").reduce((s, e) => s + Number(e.amount || 0), 0),
    [filtered]
  );
  const uniqueTxns = useMemo(
    () => new Set(filtered.map((e) => e.transaction_id)).size,
    [filtered]
  );
  const balanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const handleReverse = async (entryId, description) => {
    const reason = window.prompt(
      'Reverse entry "' + description + '"?\n\nEnter reason for reversal:',
      "Data entry correction"
    );
    if (!reason) return;
    try {
      await api.post("/ledger/entries/" + entryId + "/reverse/", { reason });
      alert("Entry reversed successfully.");
      window.location.reload();
    } catch (err) {
      alert("Reversal failed: " + (err.response?.data?.error || err.message));
    }
  };

  const toggleExpand = (txnId) => {
    setExpandedTxn(expandedTxn === txnId ? null : txnId);
  };

  return (
    <div>
      <PageHeader
        title="General Ledger"
        subtitle="Double-entry accounting — click any entry to trace the full transaction"
        action={
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { key: "balance-sheet", label: "Balance Sheet" },
              { key: "income-statement", label: "Income Statement" },
              { key: "cash-flow", label: "Cash Flow" },
              { key: "journal", label: "Journal" },
              { key: "trial-balance", label: "Trial Balance" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => { setTab(item.key); setLoading(true); setExpandedTxn(null); }}
                style={{
                  padding: "6px 14px", borderRadius: t.radius, fontFamily: t.fontBody,
                  border: `1px solid ${tab === item.key ? t.accent : t.border}`,
                  background: tab === item.key ? t.accent : t.bgCard,
                  color: tab === item.key ? t.textOnAccent : t.text,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        }
      />

      {tab === "balance-sheet" && <BalanceSheet entries={entries} loading={loading} />}
      {tab === "income-statement" && <IncomeStatement entries={entries} loading={loading} />}
      {tab === "cash-flow" && <CashFlowStatement entries={entries} loading={loading} />}

      {tab === "journal" && (
        <>
          {/* KPIs */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <StatCard label="Entries" value={filtered.length.toLocaleString()} />
            <StatCard label="Transactions" value={uniqueTxns.toLocaleString()} />
            <StatCard label="Total Debits" value={fmtUSD(totalDebits)} color={t.success} />
            <StatCard label="Total Credits" value={fmtUSD(totalCredits)} color={t.danger} />
            <StatCard
              label="Balance Check"
              value={balanced ? "Balanced" : fmtUSD(totalDebits - totalCredits)}
              color={balanced ? t.success : t.danger}
            />
          </div>

          {/* Filters */}
          <Card style={{ marginBottom: 16 }} noPad>
            <div style={{ display: "flex", gap: 10, padding: "12px 16px", flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Search description, txn ID, account, deal, entity..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: t.radius, border: `1px solid ${t.border}`,
                  background: t.bgInput, color: t.text, fontSize: 13, fontFamily: t.fontBody,
                  width: 320, outline: "none", boxSizing: "border-box",
                }}
              />
              <select
                value={filterSide}
                onChange={(e) => setFilterSide(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: t.radius, border: `1px solid ${t.border}`,
                  background: t.bgInput, color: t.text, fontSize: 13, fontFamily: t.fontBody, cursor: "pointer",
                }}
              >
                <option value="">All Sides</option>
                <option value="Debit">Debit</option>
                <option value="Credit">Credit</option>
              </select>
              <select
                value={filterAccount}
                onChange={(e) => setFilterAccount(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: t.radius, border: `1px solid ${t.border}`,
                  background: t.bgInput, color: t.text, fontSize: 13, fontFamily: t.fontBody,
                  maxWidth: 280, cursor: "pointer",
                }}
              >
                <option value="">All Accounts</option>
                {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <span style={{ color: t.textMuted, fontSize: 12, fontFamily: t.fontBody }}>
                {filtered.length} of {entries.length} entries
              </span>
              <span style={{ marginLeft: "auto" }}>
                <ExportCsvButton
                  filename="ledger-journal"
                  rows={filtered}
                  columns={[
                    { label: "Date", value: (e) => e.entry_date },
                    { label: "Transaction", value: (e) => e.transaction_id },
                    { label: "Account", value: (e) => e.account },
                    { label: "Side", value: (e) => e.side },
                    { label: "Amount", value: (e) => e.amount },
                    { label: "Currency", value: (e) => e.currency },
                    { label: "Deal", value: (e) => e.deal_name },
                    { label: "Entity", value: (e) => e.entity_name },
                    { label: "Description", value: (e) => e.description },
                  ]}
                />
              </span>
            </div>
          </Card>

          {/* Journal Table */}
          <Card noPad>
            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: t.textMuted }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: t.textMuted, fontSize: 13 }}>
                {entries.length === 0
                  ? "Ledger is empty — transactions appear here when you book trades"
                  : "No entries match your filters"}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: t.fontBody }}>
                  <thead>
                    <tr>
                      {["Date", "Txn ID", "Account", "Side", "Amount", "Description", "Entity"].map((h, i) => (
                        <th
                          key={h}
                          style={{
                            textAlign: i === 4 ? "right" : "left",
                            padding: "9px 14px", borderBottom: `2px solid ${t.border}`,
                            background: t.tableHeaderBg, color: t.textMuted,
                            fontWeight: 600, fontSize: 11, textTransform: "uppercase",
                            letterSpacing: "0.04em", whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                      <th style={{
                        padding: "9px 14px", borderBottom: `2px solid ${t.border}`,
                        background: t.tableHeaderBg, width: 60,
                      }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => {
                      const isExpanded = expandedTxn === row.transaction_id;
                      const isPartOfExpanded = expandedTxn && row.transaction_id === expandedTxn;
                      const siblings = isExpanded ? txnMap[row.transaction_id] || [] : [];

                      return (
                        <React.Fragment key={row.id}>
                          {/* Main row */}
                          <tr
                            onClick={() => toggleExpand(row.transaction_id)}
                            style={{
                              cursor: "pointer",
                              borderBottom: isExpanded ? "none" : `1px solid ${t.borderLight}`,
                              background: isPartOfExpanded ? t.accentLight : "transparent",
                              transition: t.transitionFast,
                            }}
                            onMouseEnter={(e) => {
                              if (!isPartOfExpanded) e.currentTarget.style.background = t.tableRowHover;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = isPartOfExpanded ? t.accentLight : "transparent";
                            }}
                          >
                            <td style={{ padding: "10px 14px", color: t.text, whiteSpace: "nowrap" }}>
                              {fmtDate(row.entry_date)}
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{ fontFamily: t.fontMono, fontSize: 11, color: t.info }}>
                                {row.transaction_id ? row.transaction_id.slice(0, 16) : "—"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", color: t.text, minWidth: 160 }}>
                              {row.account}
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <Badge text={row.side} variant={row.side === "Debit" ? "blue" : "amber"} />
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "right" }}>
                              <Money value={row.amount} />
                            </td>
                            <td style={{ padding: "10px 14px", color: t.text, minWidth: 200, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {row.description || "—"}
                            </td>
                            <td style={{ padding: "10px 14px", color: t.textSecondary, minWidth: 140 }}>
                              {row.entity_name || "—"}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span style={{ fontSize: 11, color: t.textMuted }}>
                                {isExpanded ? "▾" : "▸"}
                              </span>
                            </td>
                          </tr>

                          {/* Expanded transaction detail */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} style={{ padding: 0, background: t.bgSubtle, borderBottom: `1px solid ${t.border}` }}>
                                <TransactionDetail
                                  entries={siblings}
                                  currentId={row.id}
                                  txnId={row.transaction_id}
                                  onReverse={handleReverse}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === "trial-balance" && (
        <Card title="Trial Balance" subtitle="Click any account to see underlying entries">
          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: t.textMuted }}>Loading...</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: t.fontBody }}>
                <thead>
                  <tr>
                    {["Account", "Debits", "Credits", "Net", ""].map((h, i) => (
                      <th
                        key={h + i}
                        style={{
                          textAlign: h === "Account" || h === "" ? "left" : "right",
                          padding: "9px 14px", borderBottom: `2px solid ${t.border}`,
                          background: t.tableHeaderBg, color: t.textMuted,
                          fontWeight: 600, fontSize: 11, textTransform: "uppercase",
                          letterSpacing: "0.04em", width: h === "" ? 40 : "auto",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trialBalance.map((row) => {
                    const isTotal = row.account === "TOTAL";
                    const isExpanded = expandedAcct === row.account;
                    const acctEntriesForRow = isExpanded
                      ? entries.filter((e) => e.account === row.account)
                      : [];
                    // Build txn map for this account's entries
                    const acctTxnMap = {};
                    acctEntriesForRow.forEach((e) => {
                      if (!acctTxnMap[e.transaction_id]) acctTxnMap[e.transaction_id] = [];
                      acctTxnMap[e.transaction_id].push(e);
                    });

                    return (
                      <React.Fragment key={row.account}>
                        <tr
                          onClick={() => {
                            if (isTotal) return;
                            setExpandedAcct(isExpanded ? null : row.account);
                            setAcctTxnExpanded(null);
                          }}
                          style={{
                            borderBottom: isExpanded ? "none" : `1px solid ${t.borderLight}`,
                            background: isTotal ? t.bgSubtle : isExpanded ? t.accentLight : "transparent",
                            cursor: isTotal ? "default" : "pointer",
                            transition: t.transitionFast,
                          }}
                          onMouseEnter={(e) => {
                            if (!isTotal && !isExpanded) e.currentTarget.style.background = t.tableRowHover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isTotal ? t.bgSubtle : isExpanded ? t.accentLight : "transparent";
                          }}
                        >
                          <td style={{ padding: "10px 14px", fontWeight: isTotal ? 700 : 500, color: t.text }}>
                            {row.account}
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "right" }}><Money value={row.debits} /></td>
                          <td style={{ padding: "10px 14px", textAlign: "right" }}><Money value={row.credits} /></td>
                          <td style={{ padding: "10px 14px", textAlign: "right" }}><Money value={row.net} showSign /></td>
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            {!isTotal && (
                              <span style={{ fontSize: 11, color: t.textMuted }}>
                                {isExpanded ? "▾" : "▸"}
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded: all entries for this account */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} style={{ padding: 0, background: t.bgSubtle, borderBottom: `1px solid ${t.border}` }}>
                              <div style={{ padding: "12px 20px 16px" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: t.fontBody }}>
                                  {acctEntriesForRow.length} entries in {row.account}
                                </div>
                                <div style={{ background: t.bgCard, borderRadius: t.radius, border: `1px solid ${t.borderLight}`, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: t.fontBody }}>
                                    <thead>
                                      <tr>
                                        {["Date", "Txn ID", "Side", "Amount", "Description", "Entity", ""].map((h, i) => (
                                          <th key={h + i} style={{
                                            textAlign: h === "Amount" ? "right" : "left",
                                            padding: "7px 12px", borderBottom: `1px solid ${t.border}`,
                                            background: t.tableHeaderBg, color: t.textMuted,
                                            fontWeight: 600, fontSize: 10, textTransform: "uppercase",
                                            letterSpacing: "0.04em", width: h === "" ? 30 : "auto",
                                          }}>
                                            {h}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {acctEntriesForRow.map((e) => {
                                        const isTxnExpanded = acctTxnExpanded === e.transaction_id;
                                        // Get ALL entries for this transaction (across all accounts)
                                        const fullTxnEntries = isTxnExpanded
                                          ? (txnMap[e.transaction_id] || [])
                                          : [];

                                        return (
                                          <React.Fragment key={e.id}>
                                            <tr
                                              onClick={(ev) => {
                                                ev.stopPropagation();
                                                setAcctTxnExpanded(isTxnExpanded ? null : e.transaction_id);
                                              }}
                                              style={{
                                                cursor: "pointer",
                                                borderBottom: isTxnExpanded ? "none" : `1px solid ${t.borderLight}`,
                                                background: isTxnExpanded ? t.accentLight : "transparent",
                                                transition: t.transitionFast,
                                              }}
                                              onMouseEnter={(ev) => {
                                                if (!isTxnExpanded) ev.currentTarget.style.background = t.tableRowHover;
                                              }}
                                              onMouseLeave={(ev) => {
                                                ev.currentTarget.style.background = isTxnExpanded ? t.accentLight : "transparent";
                                              }}
                                            >
                                              <td style={{ padding: "7px 12px", color: t.text, whiteSpace: "nowrap" }}>
                                                {fmtDate(e.entry_date)}
                                              </td>
                                              <td style={{ padding: "7px 12px" }}>
                                                <span style={{ fontFamily: t.fontMono, fontSize: 10, color: t.info }}>
                                                  {e.transaction_id ? e.transaction_id.slice(0, 16) : "—"}
                                                </span>
                                              </td>
                                              <td style={{ padding: "7px 12px" }}>
                                                <Badge text={e.side} variant={e.side === "Debit" ? "blue" : "amber"} />
                                              </td>
                                              <td style={{ padding: "7px 12px", textAlign: "right" }}>
                                                <Money value={e.amount} />
                                              </td>
                                              <td style={{ padding: "7px 12px", color: t.text, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {e.description || "—"}
                                              </td>
                                              <td style={{ padding: "7px 12px", color: t.textMuted }}>
                                                {e.entity_name || "—"}
                                              </td>
                                              <td style={{ padding: "7px 12px", textAlign: "center" }}>
                                                <span style={{ fontSize: 10, color: t.textMuted }}>
                                                  {isTxnExpanded ? "▾" : "▸"}
                                                </span>
                                              </td>
                                            </tr>

                                            {/* Full transaction T-account */}
                                            {isTxnExpanded && (
                                              <tr>
                                                <td colSpan={7} style={{ padding: 0, borderBottom: `1px solid ${t.border}` }}>
                                                  <TransactionDetail
                                                    entries={fullTxnEntries}
                                                    currentId={e.id}
                                                    txnId={e.transaction_id}
                                                    onReverse={handleReverse}
                                                  />
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TransactionDetail — expanded view showing all GL lines in a transaction
   ═══════════════════════════════════════════════════════════════════════════ */
function TransactionDetail({ entries, currentId, txnId, onReverse }) {
  if (!entries || entries.length === 0) return null;

  const debits = entries.filter((e) => e.side === "Debit");
  const credits = entries.filter((e) => e.side === "Credit");
  const totalDebit = debits.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalCredit = credits.reduce((s, e) => s + Number(e.amount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  const isReversal = txnId && txnId.startsWith("REV-");

  // Pull context from first entry
  const sample = entries[0];
  const dealName = entries.find((e) => e.deal_name)?.deal_name;
  const entityName = entries.find((e) => e.entity_name)?.entity_name;

  return (
    <div style={{ padding: "16px 20px 20px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: t.fontBody, marginBottom: 4 }}>
            Transaction Detail
            {isReversal && (
              <Badge text="Reversal" variant="red" style={{ marginLeft: 8, verticalAlign: "middle" }} />
            )}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: t.textMuted, fontFamily: t.fontBody }}>
            <span>
              <strong style={{ color: t.textSecondary }}>Txn ID:</strong>{" "}
              <span style={{ fontFamily: t.fontMono, fontSize: 11, color: t.info }}>{txnId}</span>
            </span>
            <span>
              <strong style={{ color: t.textSecondary }}>Date:</strong> {fmtDate(sample.entry_date)}
            </span>
            <span>
              <strong style={{ color: t.textSecondary }}>Recorded:</strong> {fmtTimestamp(sample.created_at)}
            </span>
            {dealName && (
              <span>
                <strong style={{ color: t.textSecondary }}>Deal:</strong> {dealName}
              </span>
            )}
            {entityName && (
              <span>
                <strong style={{ color: t.textSecondary }}>Entity:</strong> {entityName}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Badge
            text={isBalanced ? "Balanced" : "Unbalanced"}
            variant={isBalanced ? "green" : "red"}
            dot
          />
        </div>
      </div>

      {/* GL Lines — T-account style */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {/* Debits */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase",
            letterSpacing: "0.05em", marginBottom: 6, padding: "0 4px", fontFamily: t.fontBody,
          }}>
            Debits
          </div>
          <div style={{
            background: t.bgCard, borderRadius: t.radius, border: `1px solid ${t.borderLight}`,
            overflow: "hidden",
          }}>
            {debits.map((e) => (
              <GLLine key={e.id} entry={e} isCurrent={e.id === currentId} />
            ))}
            {debits.length > 1 && (
              <div style={{
                padding: "8px 14px", borderTop: `1px solid ${t.borderLight}`, background: t.bgSubtle,
                display: "flex", justifyContent: "space-between",
                fontSize: 12, fontWeight: 700, fontFamily: t.fontMono, color: t.text,
              }}>
                <span>Total</span>
                <span>{fmtUSD(totalDebit)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Credits */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase",
            letterSpacing: "0.05em", marginBottom: 6, padding: "0 4px", fontFamily: t.fontBody,
          }}>
            Credits
          </div>
          <div style={{
            background: t.bgCard, borderRadius: t.radius, border: `1px solid ${t.borderLight}`,
            overflow: "hidden",
          }}>
            {credits.map((e) => (
              <GLLine key={e.id} entry={e} isCurrent={e.id === currentId} />
            ))}
            {credits.length > 1 && (
              <div style={{
                padding: "8px 14px", borderTop: `1px solid ${t.borderLight}`, background: t.bgSubtle,
                display: "flex", justifyContent: "space-between",
                fontSize: 12, fontWeight: 700, fontFamily: t.fontMono, color: t.text,
              }}>
                <span>Total</span>
                <span>{fmtUSD(totalCredit)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description & Reverse */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", fontFamily: t.fontBody }}>
          {sample.description || "No description"}
        </div>
        {!isReversal && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReverse(entries[0].id, sample.description || txnId);
            }}
            style={{
              fontSize: 11, padding: "4px 12px", borderRadius: t.radius,
              border: `1px solid ${t.dangerBorder}`, color: t.danger,
              background: t.dangerBg, cursor: "pointer", fontWeight: 600,
              fontFamily: t.fontBody,
            }}
          >
            Reverse Transaction
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Financial Statement Helper — computes net balance per account from entries
   ═══════════════════════════════════════════════════════════════════════════ */
function computeAccountBalances(entries) {
  const balances = {};
  entries.forEach((e) => {
    const acct = e.account;
    if (!balances[acct]) balances[acct] = 0;
    balances[acct] += e.side === "Debit" ? Number(e.amount || 0) : -Number(e.amount || 0);
  });
  return balances;
}

/* The chart's own headings, so the income statement subtotals the way the
   picker groups. Without this the P&L is a flat alphabetical list of every
   account with a balance, which was tolerable at ten accounts and is not at
   sixty. `null` while the chart is loading, and on failure — the statement
   then renders flat, exactly as it did before. */
function useChartGroups() {
  const [groups, setGroups] = useState(null);
  useEffect(() => {
    api.get("/ledger/entries/chart-of-accounts/?in_use=0")
      .then((r) => {
        const map = {};
        (r.data.sections || []).forEach((sec) =>
          (sec.groups || []).forEach((g) =>
            (g.items || []).forEach((i) => { map[i.value] = { group: g.group, label: i.label }; })
          )
        );
        setGroups(map);
      })
      .catch(() => setGroups(null));
  }, []);
  return groups;
}

/* Items bucketed under their chart heading, headings in the order the chart
   declares them, and anything the chart does not name under "Other". */
function bucketByGroup(items, chartGroups) {
  if (!chartGroups) return null;
  const order = [];
  const buckets = {};
  items.forEach((i) => {
    const g = (chartGroups[i.account] || {}).group || "Other";
    if (!buckets[g]) { buckets[g] = []; order.push(g); }
    buckets[g].push(i);
  });
  // "Other" last, whatever order it was met in — it is the residual, and a
  // residual printed in the middle of a statement reads as a category.
  order.sort((a, b) => (a === "Other") - (b === "Other"));
  return order.map((g) => ({ group: g, items: buckets[g] }));
}

function StatementLine({ item, isCredit, indent }) {
  const displayBal = (b) => (isCredit ? -b : b);
  const v = displayBal(item.balance);
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      padding: `8px 16px 8px ${indent}px`,
      borderBottom: `1px solid ${t.borderLight}`, fontSize: 13, fontFamily: t.fontBody,
    }}>
      <span style={{ color: t.text }}>{item.label || item.account}</span>
      <span style={{
        fontFamily: t.fontMono, fontSize: 12, fontWeight: 500,
        color: v >= 0 ? t.text : t.danger,
      }}>
        {v < 0 ? "-" : ""}${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

function StatementSection({ title, items, isCredit, totalLabel, chartGroups }) {
  const total = items.reduce((s, i) => s + i.balance, 0);
  // For credit-normal accounts, show as positive
  const displayBal = (b) => isCredit ? -b : b;
  const buckets = bucketByGroup(items, chartGroups);
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 13, fontWeight: 700, color: t.text, fontFamily: t.fontDisplay,
        padding: "10px 16px", background: t.bgSubtle, borderBottom: `1px solid ${t.border}`,
        borderTop: `1px solid ${t.border}`,
      }}>
        {title}
      </div>
      {buckets
        ? buckets.map(({ group, items: rows }) => {
            const sub = rows.reduce((a, i) => a + i.balance, 0);
            return (
              <div key={group}>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "8px 16px 8px 24px", borderBottom: `1px solid ${t.borderLight}`,
                  fontSize: 12, fontWeight: 600, color: t.textMuted,
                  fontFamily: t.fontBody, letterSpacing: 0.3,
                }}>
                  <span>{group}</span>
                  <span style={{ fontFamily: t.fontMono, fontSize: 11 }}>
                    {displayBal(sub) < 0 ? "-" : ""}${Math.abs(displayBal(sub)).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
                {rows.map((item) => (
                  <StatementLine key={item.account} item={item} isCredit={isCredit} indent={48} />
                ))}
              </div>
            );
          })
        : items.map((item) => (
            <StatementLine key={item.account} item={item} isCredit={isCredit} indent={32} />
          ))}
      <div style={{
        display: "flex", justifyContent: "space-between", padding: "10px 16px 10px 32px",
        borderBottom: `2px solid ${t.border}`, fontSize: 13, fontWeight: 700,
        fontFamily: t.fontBody, background: t.bgSubtle,
      }}>
        <span style={{ color: t.text }}>{totalLabel || `Total ${title}`}</span>
        <span style={{
          fontFamily: t.fontMono, fontSize: 12, fontWeight: 700,
          color: displayBal(total) >= 0 ? t.success : t.danger,
        }}>
          {displayBal(total) < 0 ? "-" : ""}${Math.abs(displayBal(total)).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Balance Sheet
   ═══════════════════════════════════════════════════════════════════════════ */
function BalanceSheet({ entries, loading }) {
  const balances = useMemo(() => computeAccountBalances(entries), [entries]);

  const categorize = (prefix) =>
    Object.entries(balances)
      .filter(([acct]) => acct.startsWith(prefix))
      .map(([acct, bal]) => ({ account: acct, label: acct.replace(prefix, "").replace(/^:/, ""), balance: bal }))
      .filter((i) => Math.abs(i.balance) > 0.01)
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));

  const assets = categorize("Assets:");
  const cashOps = Object.entries(balances)
    .filter(([a]) => a.startsWith("Cash:"))
    .map(([a, b]) => ({ account: a, label: a.replace("Cash:", ""), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01);
  const investments = Object.entries(balances)
    .filter(([a]) => a.startsWith("Investments:"))
    .map(([a, b]) => ({ account: a, label: a.replace("Investments:", ""), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01);
  const liabilities = categorize("Liabilities:");
  const distributions = Object.entries(balances)
    .filter(([a]) => a.startsWith("Distributions"))
    .map(([a, b]) => ({ account: a, label: a.replace("Distributions to Owners:", ""), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01);
  const retainedEarnings = Object.entries(balances)
    .filter(([a]) => a.startsWith("Retained"))
    .map(([a, b]) => ({ account: a, label: a.replace("Retained Earnings:", ""), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01);

  const allAssets = [...assets, ...cashOps, ...investments];
  const totalAssets = allAssets.reduce((s, i) => s + i.balance, 0);
  const totalLiabilities = liabilities.reduce((s, i) => s + i.balance, 0);

  // Equity = Income - Expenses (net income) + Retained Earnings - Distributions
  const incomeAccounts = Object.entries(balances).filter(([a]) => a.startsWith("Income:"));
  const expenseAccounts = Object.entries(balances).filter(([a]) => a.startsWith("Expense:"));
  const totalIncome = incomeAccounts.reduce((s, [, b]) => s + b, 0); // negative = credit
  const totalExpenses = expenseAccounts.reduce((s, [, b]) => s + b, 0); // positive = debit
  const netIncome = -totalIncome - totalExpenses; // Income is credit-normal
  const totalDistributions = distributions.reduce((s, i) => s + i.balance, 0);
  const totalRetained = retainedEarnings.reduce((s, i) => s + i.balance, 0);
  const totalEquity = netIncome - totalDistributions + totalRetained;

  if (loading) return <Card><div style={{ padding: 48, textAlign: "center", color: t.textMuted }}>Loading...</div></Card>;

  return (
    <Card>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: t.fontDisplay, color: t.text }}>Balance Sheet</div>
        <div style={{ fontSize: 11, color: t.textMuted }}>As of {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
      </div>

      <StatementSection title="Assets" items={allAssets} totalLabel="Total Assets" />
      <StatementSection title="Liabilities" items={liabilities} isCredit totalLabel="Total Liabilities" />

      {/* Equity section */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: t.text, fontFamily: t.fontDisplay,
          padding: "10px 16px", background: t.bgSubtle, borderBottom: `1px solid ${t.border}`,
          borderTop: `1px solid ${t.border}`,
        }}>
          Equity
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 16px 8px 32px", borderBottom: `1px solid ${t.borderLight}`, fontSize: 13 }}>
          <span style={{ color: t.text }}>Net Income (Current Period)</span>
          <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 500, color: netIncome >= 0 ? t.success : t.danger }}>
            {netIncome < 0 ? "-" : ""}${Math.abs(netIncome).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
        {distributions.length > 0 && distributions.map((d) => (
          <div key={d.account} style={{ display: "flex", justifyContent: "space-between", padding: "8px 16px 8px 32px", borderBottom: `1px solid ${t.borderLight}`, fontSize: 13 }}>
            <span style={{ color: t.text }}>Less: Distributions — {d.label}</span>
            <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 500, color: t.danger }}>
              (${Math.abs(d.balance).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
            </span>
          </div>
        ))}
        {retainedEarnings.length > 0 && retainedEarnings.map((r) => (
          <div key={r.account} style={{ display: "flex", justifyContent: "space-between", padding: "8px 16px 8px 32px", borderBottom: `1px solid ${t.borderLight}`, fontSize: 13 }}>
            <span style={{ color: t.text }}>Retained Earnings: {r.label}</span>
            <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 500, color: t.text }}>
              ${Math.abs(r.balance).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        ))}
        <div style={{
          display: "flex", justifyContent: "space-between", padding: "10px 16px 10px 32px",
          borderBottom: `2px solid ${t.border}`, fontSize: 13, fontWeight: 700, background: t.bgSubtle,
        }}>
          <span>Total Equity</span>
          <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 700, color: totalEquity >= 0 ? t.success : t.danger }}>
            {totalEquity < 0 ? "-" : ""}${Math.abs(totalEquity).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* A = L + E check */}
      <div style={{
        padding: "12px 16px", background: t.kpiBg, border: `1px solid ${t.kpiBorder}`,
        borderRadius: t.radius, margin: "0 16px 16px", display: "flex", justifyContent: "space-between",
        fontSize: 13, fontWeight: 700, fontFamily: t.fontBody,
      }}>
        <span style={{ color: t.text }}>Assets = Liabilities + Equity</span>
        <span style={{ color: Math.abs(totalAssets - (-totalLiabilities + totalEquity)) < 1 ? t.success : t.danger }}>
          ${totalAssets.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          {" = "}
          ${(-totalLiabilities + totalEquity).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          {Math.abs(totalAssets - (-totalLiabilities + totalEquity)) < 1 ? " ✓" : " ✗"}
        </span>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Income Statement
   ═══════════════════════════════════════════════════════════════════════════ */
function IncomeStatement({ entries, loading }) {
  const balances = useMemo(() => computeAccountBalances(entries), [entries]);
  const chartGroups = useChartGroups();

  // The chart's label where it has one — "Rent and office space" rather than
  // "Rent" — falling back to the account string with its prefix stripped, which
  // is all the per-name accounts have.
  const nameOf = (a, prefix) =>
    (chartGroups && chartGroups[a] && chartGroups[a].label) || a.replace(prefix, "");

  const incomeItems = Object.entries(balances)
    .filter(([a]) => a.startsWith("Income:"))
    .map(([a, b]) => ({ account: a, label: nameOf(a, "Income:"), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01)
    .sort((a, b) => a.balance - b.balance); // most negative (largest credit) first

  const expenseItems = Object.entries(balances)
    .filter(([a]) => a.startsWith("Expense:"))
    .map(([a, b]) => ({ account: a, label: nameOf(a, "Expense:"), balance: b }))
    .filter((i) => Math.abs(i.balance) > 0.01)
    .sort((a, b) => b.balance - a.balance); // largest debit first

  const totalRevenue = incomeItems.reduce((s, i) => s + i.balance, 0); // negative
  const totalExpenses = expenseItems.reduce((s, i) => s + i.balance, 0); // positive
  const netIncome = -totalRevenue - totalExpenses;

  if (loading) return <Card><div style={{ padding: 48, textAlign: "center", color: t.textMuted }}>Loading...</div></Card>;

  return (
    <Card>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: t.fontDisplay, color: t.text }}>Income Statement</div>
        <div style={{ fontSize: 11, color: t.textMuted }}>Inception to Date</div>
      </div>

      <StatementSection title="Revenue" items={incomeItems} isCredit totalLabel="Total Revenue" chartGroups={chartGroups} />
      <StatementSection title="Expenses" items={expenseItems} totalLabel="Total Expenses" chartGroups={chartGroups} />

      {/* Net Income */}
      <div style={{
        padding: "14px 16px", background: t.kpiBg, border: `1px solid ${t.kpiBorder}`,
        borderRadius: t.radius, margin: "0 16px 16px", display: "flex", justifyContent: "space-between",
        fontSize: 15, fontWeight: 700, fontFamily: t.fontDisplay,
      }}>
        <span style={{ color: t.text }}>Net Income</span>
        <span style={{
          fontFamily: t.fontMono, color: netIncome >= 0 ? t.success : t.danger,
        }}>
          {netIncome < 0 ? "-" : ""}${Math.abs(netIncome).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Cash Flow Statement
   ═══════════════════════════════════════════════════════════════════════════ */
function CashFlowStatement({ entries, loading }) {
  // Group cash-affecting entries by category
  const cashEntries = useMemo(() => {
    return entries.filter((e) =>
      e.account === "Assets:Cash" || e.account === "Cash:Operating"
    );
  }, [entries]);

  // Classify each cash transaction by its counterparty account
  const flows = useMemo(() => {
    const operating = []; // income, expenses, interest
    const investing = []; // investment purchases, sales, conversions
    const financing = []; // distributions, fronted capital, retained earnings

    // Group by transaction_id to find the non-cash side
    const txnMap = {};
    entries.forEach((e) => { if (!txnMap[e.transaction_id]) txnMap[e.transaction_id] = []; txnMap[e.transaction_id].push(e); });

    cashEntries.forEach((cashEntry) => {
      const cashAmount = cashEntry.side === "Debit" ? Number(cashEntry.amount) : -Number(cashEntry.amount);
      const siblings = (txnMap[cashEntry.transaction_id] || []).filter((e) => e.id !== cashEntry.id);
      const otherAcct = siblings.length > 0 ? siblings[0].account : "Unknown";

      const item = { description: cashEntry.description || otherAcct, amount: cashAmount, date: cashEntry.entry_date, txnId: cashEntry.transaction_id };

      if (otherAcct.startsWith("Income:") || otherAcct.startsWith("Expense:") || otherAcct.startsWith("Assets:DueFrom") || otherAcct.startsWith("Liabilities:")) {
        operating.push(item);
      } else if (otherAcct.startsWith("Distributions") || otherAcct.startsWith("Retained") || otherAcct.startsWith("Assets:Receivable")) {
        financing.push(item);
      } else {
        investing.push(item);
      }
    });

    return { operating, investing, financing };
  }, [entries, cashEntries]);

  const totalOps = flows.operating.reduce((s, i) => s + i.amount, 0);
  const totalInv = flows.investing.reduce((s, i) => s + i.amount, 0);
  const totalFin = flows.financing.reduce((s, i) => s + i.amount, 0);
  const netChange = totalOps + totalInv + totalFin;

  if (loading) return <Card><div style={{ padding: 48, textAlign: "center", color: t.textMuted }}>Loading...</div></Card>;

  const FlowSection = ({ title, items, total }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 13, fontWeight: 700, color: t.text, fontFamily: t.fontDisplay,
        padding: "10px 16px", background: t.bgSubtle, borderBottom: `1px solid ${t.border}`,
        borderTop: `1px solid ${t.border}`,
      }}>
        {title}
      </div>
      {items.length === 0 ? (
        <div style={{ padding: "8px 16px 8px 32px", fontSize: 12, color: t.textMuted }}>No activity</div>
      ) : items.map((item, i) => (
        <div key={`${item.txnId}-${i}`} style={{
          display: "flex", justifyContent: "space-between", padding: "6px 16px 6px 32px",
          borderBottom: `1px solid ${t.borderLight}`, fontSize: 12, fontFamily: t.fontBody,
        }}>
          <span style={{ color: t.textSecondary, maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.description}
          </span>
          <span style={{
            fontFamily: t.fontMono, fontWeight: 500,
            color: item.amount >= 0 ? t.success : t.danger,
          }}>
            {item.amount < 0 ? "-" : ""}${Math.abs(item.amount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
      ))}
      <div style={{
        display: "flex", justifyContent: "space-between", padding: "10px 16px 10px 32px",
        borderBottom: `2px solid ${t.border}`, fontSize: 13, fontWeight: 700, background: t.bgSubtle,
      }}>
        <span>Net {title}</span>
        <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 700, color: total >= 0 ? t.success : t.danger }}>
          {total < 0 ? "-" : ""}${Math.abs(total).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  );

  return (
    <Card>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: t.fontDisplay, color: t.text }}>Cash Flow Statement</div>
        <div style={{ fontSize: 11, color: t.textMuted }}>Inception to Date</div>
      </div>

      <FlowSection title="Operating Activities" items={flows.operating} total={totalOps} />
      <FlowSection title="Investing Activities" items={flows.investing} total={totalInv} />
      <FlowSection title="Financing Activities" items={flows.financing} total={totalFin} />

      <div style={{
        padding: "14px 16px", background: t.kpiBg, border: `1px solid ${t.kpiBorder}`,
        borderRadius: t.radius, margin: "0 16px 16px", display: "flex", justifyContent: "space-between",
        fontSize: 15, fontWeight: 700, fontFamily: t.fontDisplay,
      }}>
        <span style={{ color: t.text }}>Net Change in Cash</span>
        <span style={{ fontFamily: t.fontMono, color: netChange >= 0 ? t.success : t.danger }}>
          {netChange < 0 ? "-" : ""}${Math.abs(netChange).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
    </Card>
  );
}

/* Single GL line in the T-account view */
function GLLine({ entry, isCurrent }) {
  return (
    <div
      style={{
        padding: "8px 14px",
        borderBottom: `1px solid ${t.borderLight}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: isCurrent ? t.accentLight : "transparent",
      }}
    >
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.text, fontFamily: t.fontBody }}>
          {entry.account}
        </div>
        {entry.entity_name && (
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: t.fontBody }}>
            {entry.entity_name}
          </div>
        )}
      </div>
      <div style={{
        fontSize: 13, fontWeight: 600, fontFamily: t.fontMono,
        color: entry.side === "Debit" ? t.success : t.danger,
      }}>
        {fmtUSD(entry.amount)}
      </div>
    </div>
  );
}
